<template>
  <div dir="rtl" class="mr-8 px-4 sm:px-6 lg:px-8">
    <!-- Alert -->
    <div v-if="alert.show" 
         :class="[
           'mb-6 p-4 rounded-md border',
           alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : '',
           alert.type === 'danger' ? 'bg-red-50 border-red-200 text-red-800' : '',
           alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : ''
         ]">
      {{ alert.message }}
    </div>

    <!-- Page Header -->
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">إدارة أوقات الأذان</h1>
        <p class="mt-2 text-sm text-gray-700">إدارة أوقات الأذان للمناطق</p>
      </div>
      <div class="mt-4 sm:mt-0 sm:mr-16 sm:flex-none">
        <button
          @click="openAddModal"
          class="px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHafadhColor/90 transition-colors">
          إضافة وقت أذان جديد
        </button>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      <div class="relative">
        <input
          v-model="searchTerm"
          type="text"
          placeholder="البحث في أوقات الأذان..."
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-3"
        />
      </div>
      <div>
        <select
          v-model="filterRegionId"
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
          <option value="">جميع المناطق</option>
          <option v-for="region in regions" :key="region.id" :value="region.id">
            {{ region.regionNameAr || region.regionCode }}
          </option>
        </select>
      </div>
      <div>
        <select
          v-model="filterPrayerName"
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
          <option value="">جميع الصلوات</option>
          <option value="FAJR">الفجر</option>
          <option value="DHUHR">الظهر</option>
          <option value="ASR">العصر</option>
          <option value="MAGHRIB">المغرب</option>
          <option value="ISHA">العشاء</option>
        </select>
      </div>
      <div class="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg flex items-center">
        إجمالي أوقات الأذان: {{ athanTimes.length }}
      </div>
    </div>

    <!-- Athan Times Table -->
    <div class="mt-8 flex flex-col">
      <div class="sm:flex-auto">
        <h1 class="text-lg font-medium text-gray-900">قائمة أوقات الأذان</h1>
      </div>

      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="mt-2 text-gray-600">جاري التحميل...</p>
      </div>

      <div v-else-if="filteredAthanTimes.length === 0" class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <p class="mt-2 text-gray-600">
          {{ searchTerm || filterRegionId || filterPrayerName ? 'لا توجد أوقات أذان تطابق البحث' : 'لا توجد أوقات أذان مسجلة' }}
        </p>
      </div>

      <div v-else class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table class="min-w-full divide-y divide-customHafadhColor">
              <thead class="bg-customHafadhColor text-center">
                <tr>
                  <th class="py-3.5 pr-4 pl-3 text-right text-sm font-semibold text-gray-900 sm:pr-6">#</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">المنطقة</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">اسم الصلاة</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">وقت الصلاة</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">بدء الجلسة بعد (دقيقة)</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">عدد الفصول</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">تعديل</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">حذف</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr v-for="(athanTime, index) in filteredAthanTimes" :key="athanTime.id">
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm text-gray-900 sm:pr-6">{{ index + 1 }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">{{ athanTime.regionNameAr || '-' }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ getArabicPrayerName(athanTime.prayerName) }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ athanTime.prayerTime || '-' }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ athanTime.sessionStartAfter || 0 }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ athanTime.totalClass || '-' }}</td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button class="enabled-button" @click="openEditModal(athanTime)">تعديل</button>
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button 
                      class="text-red-600 hover:text-red-800" 
                      @click="handleDelete(athanTime)">
                      حذف
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.totalPages > 1" class="mt-6 flex items-center justify-between">
        <div class="text-sm text-gray-700">
          عرض {{ pagination.from }} إلى {{ pagination.to }} من {{ pagination.total }} نتائج
        </div>
        <div class="flex gap-2">
          <button
            @click="changePage(pagination.currentPage - 1)"
            :disabled="pagination.currentPage === 1"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            السابق
          </button>
          <button
            @click="changePage(pagination.currentPage + 1)"
            :disabled="pagination.currentPage === pagination.totalPages"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            التالي
          </button>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="showModal" class="popup" dir="rtl">
      <div class="popup-content" style="max-width: 600px;">
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
          {{ editMode ? 'تعديل وقت الأذان' : 'إضافة وقت أذان جديد' }}
        </h3>
        
        <form @submit.prevent="handleSave">
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label for="region_id" class="block text-sm font-medium text-gray-700 mb-2">المنطقة *</label>
              <select
                v-model="formData.region_id"
                id="region_id"
                @change="loadPrayerTimeForNewAthan"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                <option value="">الرجاء اختيار المنطقة</option>
                <option v-for="region in regions" :key="region.id" :value="region.id">
                  {{ region.regionNameAr || region.regionCode }}
                </option>
              </select>
            </div>
            <div>
              <label for="prayer_name" class="block text-sm font-medium text-gray-700 mb-2">اسم الصلاة *</label>
              <select
                v-model="formData.prayer_name"
                id="prayer_name"
                @change="loadPrayerTimeForNewAthan"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                <option value="">الرجاء اختيار الصلاة</option>
                <option value="FAJR">الفجر</option>
                <option value="DHUHR">الظهر</option>
                <option value="ASR">العصر</option>
                <option value="MAGHRIB">المغرب</option>
                <option value="ISHA">العشاء</option>
              </select>
            </div>
            <div>
              <label for="prayer_time" class="block text-sm font-medium text-gray-700 mb-2">وقت الصلاة *</label>
              <input
                v-model="formData.prayer_time"
                type="time"
                id="prayer_time"
                required
                step="1"
                :disabled="loadingPrayerTime"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <p v-if="loadingPrayerTime" class="mt-1 text-xs text-blue-500">جاري جلب وقت الصلاة...</p>
              <p v-else-if="formData.prayer_time && !editMode" class="mt-1 text-xs text-green-500">✓ تم جلب وقت الصلاة تلقائياً</p>
              <p v-else class="mt-1 text-xs text-gray-500">تنسيق: HH:MM:SS (مثال: 05:30:00)</p>
            </div>
            <div>
              <label for="session_start_after" class="block text-sm font-medium text-gray-700 mb-2">بدء الجلسة بعد (دقيقة)</label>
              <input
                v-model.number="formData.session_start_after"
                type="number"
                id="session_start_after"
                min="0"
                placeholder="0"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label for="total_class" class="block text-sm font-medium text-gray-700 mb-2">عدد الفصول</label>
              <input
                v-model.number="formData.total_class"
                type="number"
                id="total_class"
                min="1"
                placeholder="عدد الفصول"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          <div class="mt-6 flex gap-3 justify-end">
            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
              إلغاء
            </button>
            <button
              type="submit"
              :disabled="saving"
              class="px-4 py-2 bg-customHafadhColor text-white rounded-md text-sm font-medium hover:bg-customHafadhColor/90 disabled:opacity-50">
              {{ saving ? 'جاري الحفظ...' : (editMode ? 'تحديث' : 'إضافة') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import axios from 'axios'
import url from '@/url.js'

export default defineComponent({
  name: 'AthanTimesManagement',
  data() {
    return {
      athanTimes: [],
      regions: [],
      loading: false,
      saving: false,
      searchTerm: '',
      filterRegionId: '',
      filterPrayerName: '',
      showModal: false,
      editMode: false,
      formData: {
        id: null,
        region_id: '',
        prayer_name: '',
        prayer_time: '',
        session_start_after: 0,
        total_class: null,
      },
      alert: {
        show: false,
        type: 'success',
        message: '',
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        total: 0,
        from: 0,
        to: 0,
      },
      loadingPrayerTime: false,
    }
  },
  computed: {
    filteredAthanTimes() {
      let filtered = this.athanTimes

      if (this.searchTerm) {
        const search = this.searchTerm.toLowerCase()
        filtered = filtered.filter(athanTime => 
          athanTime.regionNameAr?.toLowerCase().includes(search) ||
          athanTime.prayerName?.toLowerCase().includes(search) ||
          athanTime.prayerTime?.includes(search)
        )
      }

      if (this.filterRegionId) {
        filtered = filtered.filter(athanTime => athanTime.regionId == this.filterRegionId)
      }

      if (this.filterPrayerName) {
        filtered = filtered.filter(athanTime => athanTime.prayerName === this.filterPrayerName)
      }

      return filtered
    }
  },
  watch: {
    searchTerm() {
      this.pagination.currentPage = 1
    },
    filterRegionId() {
      this.pagination.currentPage = 1
      this.loadAthanTimes()
    },
    filterPrayerName() {
      this.pagination.currentPage = 1
      this.loadAthanTimes()
    },
  },
  mounted() {
    this.loadRegions()
    this.loadAthanTimes()
  },
  methods: {
    async loadRegions() {
      try {
        const { data } = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.regions_list,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') }
          }
        )

        if (data.result && data.result.data) {
          if (Array.isArray(data.result.data)) {
            this.regions = data.result.data
          } else if (data.result.data.data && Array.isArray(data.result.data.data)) {
            this.regions = data.result.data.data
          }
        }
      } catch (error) {
        console.error('Failed to load regions', error)
      }
    },
    async loadAthanTimes() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          size: 20,
        }
        if (this.searchTerm) {
          params.search = this.searchTerm
        }
        if (this.filterRegionId) {
          params.region_id = this.filterRegionId
        }
        if (this.filterPrayerName) {
          params.prayer_name = this.filterPrayerName
        }

        const { data } = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.athan_times_list,
          {
            params,
            headers: { 'X-Authorization': localStorage.getItem('token') }
          }
        )

        // Handle nested response structure
        if (data.result && data.result.data) {
          if (Array.isArray(data.result.data)) {
            this.athanTimes = data.result.data
          } else if (data.result.data.data && Array.isArray(data.result.data.data)) {
            this.athanTimes = data.result.data.data
            if (data.result.data.pagination) {
              this.pagination = {
                currentPage: data.result.data.pagination.current_page || 1,
                totalPages: data.result.data.pagination.total_pages || 1,
                total: data.result.data.pagination.total || 0,
                from: data.result.data.pagination.from || 0,
                to: data.result.data.pagination.to || 0,
              }
            }
          } else {
            this.athanTimes = []
          }
        } else {
          this.athanTimes = []
        }
      } catch (error) {
        console.error('Failed to load athan times', error)
        this.showAlert('danger', 'فشل في تحميل أوقات الأذان')
        this.athanTimes = []
      } finally {
        this.loading = false
      }
    },
    openAddModal() {
      this.editMode = false
      this.formData = {
        id: null,
        region_id: '',
        prayer_name: '',
        prayer_time: '',
        session_start_after: 0,
        total_class: null,
      }
      this.loadingPrayerTime = false
      this.showModal = true
    },
    openEditModal(athanTime) {
      this.editMode = true
      // Convert prayer_time from H:i:s to format suitable for time input
      let prayerTime = athanTime.prayerTime || ''
      if (prayerTime && prayerTime.includes(':')) {
        // Ensure it's in HH:MM:SS format for time input
        const parts = prayerTime.split(':')
        if (parts.length === 2) {
          prayerTime = prayerTime + ':00'
        }
      }
      
      this.formData = {
        id: athanTime.id,
        region_id: athanTime.regionId || '',
        prayer_name: athanTime.prayerName || '',
        prayer_time: prayerTime,
        session_start_after: athanTime.sessionStartAfter || 0,
        total_class: athanTime.totalClass || null,
      }
      this.loadingPrayerTime = false
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
      this.editMode = false
      this.formData = {
        id: null,
        region_id: '',
        prayer_name: '',
        prayer_time: '',
        session_start_after: 0,
        total_class: null,
      }
    },
    async handleSave() {
      this.saving = true
      try {
        // Convert time input to H:i:s format
        let prayerTime = this.formData.prayer_time
        if (prayerTime && !prayerTime.includes(':')) {
          prayerTime = prayerTime + ':00'
        }

        const payload = {
          region_id: parseInt(this.formData.region_id),
          prayer_name: this.formData.prayer_name,
          prayer_time: prayerTime,
          session_start_after: this.formData.session_start_after || 0,
          total_class: this.formData.total_class || null,
        }

        if (this.editMode) {
          await axios.put(
            process.env.VUE_APP_SERVICE_URL + url.athan_times_update + this.formData.id,
            payload,
            { headers: { 'X-Authorization': localStorage.getItem('token') } }
          )
          this.showAlert('success', 'تم تحديث وقت الأذان بنجاح')
        } else {
          await axios.post(
            process.env.VUE_APP_SERVICE_URL + url.athan_times_create,
            payload,
            { headers: { 'X-Authorization': localStorage.getItem('token') } }
          )
          this.showAlert('success', 'تم إضافة وقت الأذان بنجاح')
        }

        this.closeModal()
        this.loadAthanTimes()
      } catch (error) {
        console.error('Failed to save athan time', error)
        const message = error.response?.data?.result?.mesAr || error.response?.data?.mesAr || 'فشل في حفظ وقت الأذان'
        this.showAlert('danger', message)
      } finally {
        this.saving = false
      }
    },
    async handleDelete(athanTime) {
      const prayerName = this.getArabicPrayerName(athanTime.prayerName)
      if (!confirm(`هل أنت متأكد من حذف وقت الأذان "${prayerName}" للمنطقة "${athanTime.regionNameAr}"؟`)) {
        return
      }

      try {
        await axios.delete(
          process.env.VUE_APP_SERVICE_URL + url.athan_times_delete + athanTime.id,
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        )
        this.showAlert('success', 'تم حذف وقت الأذان بنجاح')
        this.loadAthanTimes()
      } catch (error) {
        console.error('Failed to delete athan time', error)
        const message = error.response?.data?.result?.mesAr || error.response?.data?.mesAr || 'فشل في حذف وقت الأذان'
        this.showAlert('danger', message)
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.pagination.currentPage = page
        this.loadAthanTimes()
      }
    },
    showAlert(type, message) {
      this.alert = { show: true, type, message }
      setTimeout(() => {
        this.alert.show = false
      }, 5000)
    },
    getArabicPrayerName(prayerName) {
      const prayerNames = {
        'FAJR': 'الفجر',
        'DHUHR': 'الظهر',
        'ASR': 'العصر',
        'MAGHRIB': 'المغرب',
        'ISHA': 'العشاء',
      }
      return prayerNames[prayerName] || prayerName || 'غير محدد'
    },
    async loadPrayerTimeForNewAthan() {
      // Only load if both region and prayer are selected, and not in edit mode
      if (this.editMode || !this.formData.region_id || !this.formData.prayer_name) {
        if (!this.editMode && (!this.formData.region_id || !this.formData.prayer_name)) {
          this.formData.prayer_time = ''
        }
        return
      }

      this.loadingPrayerTime = true
      try {
        // Fetch prayer time directly from Aladhan API
        const { data } = await axios.get(
          `${process.env.VUE_APP_SERVICE_URL}${url.get_prayer_time_from_aladhan}${this.formData.region_id}/prayer-time/${this.formData.prayer_name}`,
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        )

        if (data?.result?.data?.prayerTime) {
          // Convert HH:mm:ss to HH:mm for time input
          const timeParts = data.result.data.prayerTime.split(':')
          this.formData.prayer_time = `${timeParts[0]}:${timeParts[1]}`
          console.log(`Fetched prayer time from Aladhan API: ${this.formData.prayer_name} = ${this.formData.prayer_time}`)
        } else {
          // Prayer time not found
          this.formData.prayer_time = ''
          console.warn(`Prayer time not found for ${this.formData.prayer_name} in region ${this.formData.region_id}`)
        }
      } catch (error) {
        console.error('Error loading prayer time from Aladhan API:', error)
        this.formData.prayer_time = ''
        // Show user-friendly error message
        if (error.response?.data?.result?.mesAr) {
          this.showAlert('danger', `خطأ في جلب وقت الصلاة: ${error.response.data.result.mesAr}`)
        }
      } finally {
        this.loadingPrayerTime = false
      }
    },
    getPrayerNameFromValue(value) {
      // Normalize prayer name for comparison
      const prayerMap = {
        'FAJR': 'الفجر',
        'DHUHR': 'الظهر',
        'ASR': 'العصر',
        'MAGHRIB': 'المغرب',
        'ISHA': 'العشاء'
      }
      return prayerMap[value] || value
    },
  },
})
</script>

<style scoped>
.popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.popup-content {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.enabled-button {
  color: #3b82f6;
  font-weight: 500;
  cursor: pointer;
}

.enabled-button:hover {
  color: #2563eb;
  text-decoration: underline;
}
</style>

